import { useEffect, useState, type ReactNode } from "react";
import { onAuthStateChanged, type User } from "firebase/auth";
import { auth, ALLOWED_EMAIL_DOMAIN, logOut } from "@/lib/firebase";
import { Login } from "@/components/login";

type AuthState = {
  user: User | null;
  loading: boolean;
};

export function AuthGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ user: null, loading: true });

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      const domain = u?.email?.split("@")[1]?.toLowerCase();
      if (u && domain !== ALLOWED_EMAIL_DOMAIN) {
        // Existing session from a non-allowed domain — sign out.
        logOut().catch(() => {});
        setState({ user: null, loading: false });
      } else {
        setState({ user: u, loading: false });
      }
    });
    return () => unsub();
  }, []);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
        <div className="flex items-center gap-3">
          <span className="flex gap-1">
            <span className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-pulse" />
            <span className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-pulse [animation-delay:120ms]" />
            <span className="w-2 h-2 bg-muted-foreground/60 rounded-full animate-pulse [animation-delay:240ms]" />
          </span>
          <span className="text-sm">Loading…</span>
        </div>
      </div>
    );
  }

  if (!state.user) return <Login />;

  return <>{children}</>;
}
